import jwtDecode from "jwt-decode";
import { createContext, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { apiUrls } from "../helpers/apiUrls";
import { makeRequest } from "../helpers/makeRequest";
import { isAppLocal } from "../helpers/methods";
import { prepareUrlWithQueryParams } from "../helpers/methods";
import useLocalStorage from "../hooks/useLocalStorage";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [accessToken, setAccessToken, removeAccessToken] = useLocalStorage("accessToken");

    const [user, setUser] = useState(() => (accessToken ? jwtDecode(accessToken) : null));

    const navigate = useNavigate();
    const location = useLocation();

    const login = (username, password) => {
        return makeRequest(axios, apiUrls.users.auth.loginUrl, {
            method: "POST",
            data: { username, password },
        }).then((data) => {
            setAccessToken(data?.access);
            setUser(jwtDecode(data?.access));

            const from = location.state?.from || "/";

            navigate(from, { replace: true });
        });
    };

    const aadLogin = (accessToken, redirectUrl) => {
        return makeRequest(axios, apiUrls.users.aadAuth.login, {
            method: "POST",
            data: { access_token: accessToken },
        }).then((data) => {
            setAccessToken(data?.access);
            setUser(jwtDecode(data?.access));

            const from = redirectUrl || location.state?.from || "/";
            navigate(from, { replace: true });
        });
    };

    const refreshAccess = async () => {
        try {
            const data = await makeRequest(axios, apiUrls.users.auth.refreshTokenUrl, {
                method: "POST",
                data: {},
            });
            const access = await data.access;
            setAccessToken(access);
            setUser(jwtDecode(access));

            return access;
        } catch {
            // TODO Uncomment when JG made fixes
            removeAccessToken();
            setUser(null);
        }
    };

    const logout = (redirectToMyHomeLandingPage = false, redirectToMyHomeInvestment = false) => {
        const logoutCallback = isAppLocal()
            ? () => navigate("/external_login/")
            : redirectToMyHomeInvestment 
                ? redirectToMyHomeInvestmentPage
                : redirectToMyHomeLandingPage
                    ? redirectToMyHome
                    : redirectToMyHomeLogoutPage;
        return makeRequest(axios, apiUrls.users.auth.logoutUrl, {
            method: "POST",
            data: {},
        }).then(() => {
            setUser(null);
            removeAccessToken(null);
            sessionStorage.removeItem("postponedBookingsChecked");
            logoutCallback();
        });
    };

    const logoutAndGoBackToMyHome = () => {
        if (isAppLocal()) {
            return;
        }

        return logout(true, false);
    };

    const logoutAndGoToMyHomeInvestment = () => {
        if (isAppLocal()) {
            return;
        }

        return logout(false, true);
    };

    const myHomeLogin = (accessToken) => {
        return makeRequest(axios, apiUrls.users.myHomeAuth.login, {
            method: "POST",
            data: { access_token: accessToken },
        })
            .then((data) => {
                setAccessToken(data?.access);
                setUser(jwtDecode(data?.access));
            })
            .catch((err) => {
                removeAccessToken();
                setUser(null);
                return Promise.reject(err);
            });
    };

    const redirectToMyHome = (withRedirection, redirectUrl = undefined) => {
        const myHomePageUrl = window._env_.MY_HOME_FRONTEND_URL;
        const urlParams = {};
        if (withRedirection) {
            redirectUrl = redirectUrl ?? `${window.origin}${location.state?.from?.pathname || "/"}`;
            urlParams.redirectUrl = redirectUrl;
        }

        window.location.replace(prepareUrlWithQueryParams(myHomePageUrl, urlParams));
    };

    const redirectToMyHomeLogoutPage = () =>
        window.location.replace(`${window._env_.MY_HOME_FRONTEND_URL}logout/`);

    const redirectToMyHomeInvestmentPage = () => 
        window.location.replace(`${window._env_.MY_HOME_FRONTEND_URL}investments/`);

    const getMyHomeSelectedPage = (pageName) => `${window._env_.MY_HOME_FRONTEND_URL}${pageName}`;

    const navigateToLogoutRoute = () => navigate("/logout/");
    const navigateToRedirectBackToMyHomeRoute = () => navigate("/back_to_myhome/");
    const navigateToRedirectBackToMyHomeInvestment = () => navigate("/to_myhome_investment/");

    const contextData = {
        user,
        accessToken,
        refreshAccess,
        login,
        logout,
        logoutAndGoBackToMyHome,
        logoutAndGoToMyHomeInvestment,
        aadLogin,
        myHomeLogin,
        redirectToMyHome,
        navigateToLogoutRoute,
        getMyHomeSelectedPage,
        navigateToRedirectBackToMyHomeRoute,
    };

    return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
    return useContext(AuthContext);
}
